import { useState, useRef, useEffect, useMemo } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiX, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { eventService } from '../services/apiService';
import { getImageSrc } from '../utils/imageHelper';
import EventForm from './EventForm';

const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

const truncate = (str, max = 80) =>
  str && str.length > max ? str.slice(0, max).trimEnd() + '…' : str;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
};

const isUpcoming = (event) => {
  if (!event.eventDate) return true; // no date → treat as upcoming
  return new Date(event.eventDate) >= new Date();
};

const EventList = ({ events = [], fetchEvents }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'upcoming' | 'past'
  const [sortKey, setSortKey] = useState('date'); // 'title' | 'date'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const panelRef = useRef(null);

  // Counts
  const upcomingCount = useMemo(() => events.filter(isUpcoming).length, [events]);
  const pastCount = useMemo(() => events.filter((e) => !isUpcoming(e)).length, [events]);

  // Filter + sort
  const displayEvents = useMemo(() => {
    let list = [...events];

    if (filter === 'upcoming') list = list.filter(isUpcoming);
    else if (filter === 'past') list = list.filter((e) => !isUpcoming(e));

    list.sort((a, b) => {
      let valA, valB;
      if (sortKey === 'title') {
        valA = (a.title || '').toLowerCase();
        valB = (b.title || '').toLowerCase();
      } else {
        // date: null/undefined sorts last
        valA = a.eventDate ? new Date(a.eventDate).getTime() : Infinity;
        valB = b.eventDate ? new Date(b.eventDate).getTime() : Infinity;
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [events, filter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <FiChevronUp className="opacity-20 w-3 h-3" />;
    return sortDir === 'asc'
      ? <FiChevronUp className="w-3 h-3 text-[#166534]" />
      : <FiChevronDown className="w-3 h-3 text-[#166534]" />;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    setIsDeleting(true);
    try {
      await eventService.deleteEvent(id);
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (showForm && panelRef.current) {
      setTimeout(() => {
        panelRef.current.style.transform = 'translateX(0)';
      }, 10);
    }
  }, [showForm]);

  const closePanel = () => {
    if (panelRef.current) {
      panelRef.current.style.transform = 'translateX(100%)';
      setTimeout(() => setShowForm(false), 300);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden relative">
      {/* ── Header ───────────────────────────────── */}
      <div className="px-4 py-4 sm:px-6 border-b border-gray-200 space-y-3">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Events</h3>
          <button
            onClick={() => { setSelectedEvent(null); setShowForm(true); }}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
          >
            <FiPlus className="mr-1" /> Create Event
          </button>
        </div>

        {/* Filter + Sort controls */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Filter tabs */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            {[
              { key: 'all', label: `All (${events.length})` },
              { key: 'upcoming', label: `Upcoming (${upcomingCount})` },
              { key: 'past', label: `Past (${pastCount})` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  filter === key
                    ? 'bg-[#166534] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Sort select */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 text-xs font-medium">Sort by</span>
            <select
              value={`${sortKey}-${sortDir}`}
              onChange={(e) => {
                const [k, d] = e.target.value.split('-');
                setSortKey(k);
                setSortDir(d);
              }}
              className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#166534]/30"
            >
              <option value="date-asc">Date (earliest first)</option>
              <option value="date-desc">Date (latest first)</option>
              <option value="title-asc">Title (A → Z)</option>
              <option value="title-desc">Title (Z → A)</option>
            </select>
          </div>

          <span className="text-xs text-gray-400 ml-auto">
            {displayEvents.length} event{displayEvents.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* ── Table ────────────────────────────────── */}
      <div className="overflow-x-auto">
        {displayEvents.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            No events found for the selected filter.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Image
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => toggleSort('title')}
                    className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                  >
                    Title <SortIcon col="title" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => toggleSort('date')}
                    className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                  >
                    Event Date <SortIcon col="date" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayEvents.map((event) => {
                const upcoming = isUpcoming(event);
                const desc = truncate(
                  stripHtml(event.content) || (event.paragraphs?.[0] ?? '')
                );
                return (
                  <tr key={event._id} className="hover:bg-gray-50 transition-colors">
                    {/* Thumbnail */}
                    <td className="px-4 py-3">
                      {event.thumbnail ? (
                        <img
                          src={getImageSrc(event.thumbnail)}
                          alt="thumb"
                          className="h-12 w-16 object-contain rounded border border-gray-100 bg-gray-50"
                        />
                      ) : (
                        <div className="h-12 w-16 rounded border border-gray-100 bg-gray-50" />
                      )}
                    </td>

                    {/* Title + description */}
                    <td className="px-4 py-3 max-w-xs">
                      <div className="text-sm font-semibold text-gray-900 leading-snug">
                        {event.title}
                      </div>
                      {desc && (
                        <div className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                          {desc}
                        </div>
                      )}
                    </td>

                    {/* Event date */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {event.eventDate ? (
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            {formatDate(event.eventDate)}
                          </div>
                          <div className="text-xs text-gray-400">
                            {formatTime(event.eventDate)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">No date set</span>
                      )}
                    </td>

                    {/* Status badge */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                        upcoming
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                      }`}>
                        {upcoming ? 'Upcoming' : 'Past'}
                      </span>
                    </td>

                    {/* Tags */}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {event.tags?.slice(0, 3).map((tag, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 text-xs rounded-full bg-[#166534]/10 text-[#0e3d1f] font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                        {(event.tags?.length ?? 0) > 3 && (
                          <span className="text-xs text-gray-400">
                            +{event.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => { setSelectedEvent(event); setShowForm(true); }}
                        className="text-[#166534] hover:text-[#0e3d1f] mr-3 p-1 rounded hover:bg-[#166534]/10 transition-colors"
                        title="Edit"
                      >
                        <FiEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(event._id)}
                        disabled={isDeleting}
                        className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Slide-out Panel ──────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
              onClick={closePanel}
            />
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <div
                ref={panelRef}
                className="relative w-screen max-w-md transform transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                style={{ transform: 'translateX(calc(100% + 20px))', marginLeft: '-20px' }}
              >
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedEvent ? 'Edit Event' : 'Create New Event'}
                    </h3>
                    <button
                      onClick={closePanel}
                      className="text-gray-400 hover:text-gray-500 transition-colors"
                    >
                      <FiX className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="flex-1 p-4">
                    <EventForm
                      event={selectedEvent}
                      onClose={closePanel}
                      onSave={() => { fetchEvents(); closePanel(); }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventList;
